import Vue from 'vue';
import axios from "axios";
import eventsOddsHandler from "@/services/eventsOddsHandlerV3";
import {loadPinnedMarkets} from '@/services/pinnedMarketsStorage';
import assign from 'lodash/assign';
import {injectGoalIfScoreChanged} from '@/services/goalDetector';
import {reapplyKeyOnMarkets} from '@/services/keyLiveReapply.service';
import {sync as syncFrozen} from '@/services/frozenOddsRegistry.service';

//version 3.2 Exact Timestamp Monotonic Livescore & Odds Fix

// --- CONSTANTS ---
const EVENT_STATUS = {
    PREMATCH: 1,
    LIVE: 2,
    FINISHED: 3,
    STARTING_SOON: 9
};

const CLOCK_STATUS = {
    RUNNING: 1,
    STOPPED: 2
};

// --- LOCAL LOOKUP TABLES ---
let eventsDetailLookup = {};
let oddsDetailLookup = {};

// --- GOAL BADGE TIMERS ---
const _goalTimers = new Map();

// --- TYPE CHANGE RESET TIMER ---
let _typeChangeResetTimer = null;
const TYPE_CHANGE_RESET_DELAY = 2500;

function _scheduleTypeChangeReset(commit) {
    if (_typeChangeResetTimer) clearTimeout(_typeChangeResetTimer);
    _typeChangeResetTimer = setTimeout(() => {
        commit('RESET_TYPE_CHANGES');
        _typeChangeResetTimer = null;
    }, TYPE_CHANGE_RESET_DELAY);
}

// --- HELPER: MONOTONIC UPDATE ---
// Estrae il timestamp in millisecondi in modo sicuro sia dal root che dall'oggetto livescore
function extractLastUpdate(payload) {
    if (!payload) return 0;
    // 1. lastUpdate at root (es. dal nuovo formato socket o dal polling root)
    if (payload.lastUpdate) return new Date(payload.lastUpdate).getTime();

    // 2. lastUpdate inside livescore object or array
    if (payload.livescore) {
        const ls = Array.isArray(payload.livescore) ? payload.livescore[0] : payload.livescore;
        if (ls && ls.lastUpdate) {
            return new Date(ls.lastUpdate).getTime();
        }
    }
    return 0;
}

function isPayloadStale(existing, incoming) {
    if (!existing || !incoming) return false;

    const exStatus = parseInt(existing.status || existing.livescore?.scoreboard?.status || '0');

    let inStatus = parseInt(incoming.status || '0');
    if (inStatus === 0 && incoming.livescore) {
        const inLs = Array.isArray(incoming.livescore) ? incoming.livescore[0] : incoming.livescore;
        inStatus = parseInt(inLs?.scoreboard?.status || '0');
    }

    if ((inStatus === EVENT_STATUS.PREMATCH || inStatus === EVENT_STATUS.STARTING_SOON) &&
        (exStatus === EVENT_STATUS.LIVE || exStatus === EVENT_STATUS.FINISHED)) {
        return true;
    }
    if (inStatus === EVENT_STATUS.LIVE && exStatus === EVENT_STATUS.FINISHED) {
        return true;
    }

    const exTime = extractLastUpdate(existing);
    const inTime = extractLastUpdate(incoming);

    if (exTime > 0 && inTime > 0 && inTime < exTime) {
        return true;
    }

    return false;
}

function mapDetailOddsToLookup(market, eventId) {
    if (!market || !market.outcome || !Array.isArray(market.outcome)) return;
    for (let k = 0; k < market.outcome.length; k++) {
        const odd = market.outcome[k];
        if (odd && odd.unique_id) {
            oddsDetailLookup[odd.unique_id] = {odd, market, eventId: eventId || null};
            // FRZ.2 — keep the shared frozen-uid registry in sync alongside
            // the per-store lookup so cart.socketClearSuspendedOnRecover
            // can consult O(1) whether an outcome is currently frozen.
            syncFrozen(odd.unique_id, odd.frozenApplied === true);
        }
    }
}

/**
 * Called when the silent-refresh API returns no event for `source`.
 * Calls status-check and combines it with the last known local livescore period.
 * If status = 3 OR period >= 100 (match finished) → show badge in place.
 * If it's suspended, awaiting OT, etc. → remove + alert.
 */
async function _handleDetailEventMissing(context, source, apiUrl, token) {
    const item = context.state.eventsLiveDetail.find(
        e => e.source === source || (e.event && e.event.id === source)
    );
    const provider = item?.event?.provider || 'lsport';

    try {
        const res = await axios.post(
            `${apiUrl}/v3/fixtures/status-check`,
            {events: [{provider, eventId: String(source)}]},
            {headers: {Authorization: `Bearer ${token}`}}
        );
        const result = (res.data?.data || []).find(e => String(e.eventId) === String(source));

        const cachedEvent = item?.event || item;
        const currentPeriod = parseInt(cachedEvent?.livescore?.scoreboard?.currentPeriod || '0');

        const isStatusFinished = result && parseInt(result.status) === EVENT_STATUS.FINISHED;
        const isPeriodFinished = currentPeriod >= 100;

        if (isStatusFinished || isPeriodFinished) {
            context.commit('MARK_EVENT_LIVE_DETAIL_FINISHED', source);
        } else {
            context.dispatch('showAlertEventNotAvailable', source);
            context.commit('REMOVE_EVENT_LIVE_DETAIL', source);
        }
    } catch (_) {
        context.dispatch('showAlertEventNotAvailable', source);
        context.commit('REMOVE_EVENT_LIVE_DETAIL', source);
    }
}

/**
 * Builds the request body for /v3/events/live/event-detail.
 * Shared by fetchEventLiveDetailV5, fetchOddsGroupDetailLiveV3, silentRefreshLiveDetail.
 */
function _buildDetailBody(context, eventId, sportId, tournamentId, groupId, marketId, competitors) {
    const body = {
        eventId,
        platformId: context.rootGetters['settings/getPlatformId'],
        type: 'groupOdds',
        section: 'sport',
        typegroup: 'detail',
        sportId: sportId || '',
        tournamentId: tournamentId || '',
        groupId: groupId === 0 ? null : (groupId ?? null),
        lang: context.rootGetters['settings/getLanguage'],
        product_id: 1,
        limit: context.rootGetters['settings/getLiveDetailMaxMarkets'] || 500
    };
    if (marketId != null) body.marketId = marketId;
    if (competitors) {
        if (competitors.homeId) body.competitorHomeId = competitors.homeId;
        if (competitors.awayId) body.competitorAwayId = competitors.awayId;
        if (competitors.home) body.competitorHome = competitors.home;
        if (competitors.away) body.competitorAway = competitors.away;
        if (competitors.sportName) body.sportName = competitors.sportName;
        if (competitors.categoryName) body.categoryName = competitors.categoryName;
        if (competitors.tournamentName) body.tournamentName = competitors.tournamentName;
    }
    return body;
}

const state = {
    eventsLiveDetail: [],
    eventsLiveDetailActive: [],
    eventsLiveDetailParams: {},
    loading: false,
    marketsGroupsDetailLive: [],
    availableMarkets: []
};

const getters = {
    getEventsLiveDetail: (state) => state.eventsLiveDetail,
    getEventsLiveDetailActive: (state) => state.eventsLiveDetailActive,
    getLoadingDetail: (state) => state.loading,
    getMarketsGroupsDetailLive: (state) => state.marketsGroupsDetailLive,
    getAvailableMarketsLive: (state) => state.availableMarkets
};

const mutations = {
    SET_LOADING(state, status) {
        state.loading = status;
    },

    SET_AVAILABLE_MARKETS(state, list) {
        state.availableMarkets = list;
    },

    SET_EVENT_LIVE_DETAIL(state, payload) {
        state.eventsLiveDetail = payload;

        eventsDetailLookup = {};
        oddsDetailLookup = {};
        _goalTimers.forEach(timerId => clearTimeout(timerId));
        _goalTimers.clear();

        if (payload && Array.isArray(payload)) {
            payload.forEach(obj => {
                if (obj.event && obj.event.id) {
                    eventsDetailLookup[obj.event.id] = obj.event;

                    if (obj.event.odds && Array.isArray(obj.event.odds)) {
                        obj.event.odds.forEach(marketGroup => {
                            if (Array.isArray(marketGroup)) {
                                marketGroup.forEach(m => mapDetailOddsToLookup(m, obj.event.id));
                            } else {
                                mapDetailOddsToLookup(marketGroup, obj.event.id);
                            }
                        });
                    }
                }
            });
        }
    },

    UPDATE_ODDS_EVENT_LIVE_DETAIL(state, payload) {
        const targetId = payload.source || (payload.event && payload.event.id);
        const index = state.eventsLiveDetail.findIndex(e => e.source === targetId || (e.event && e.event.id === targetId));

        if (index !== -1) {
            const detail = state.eventsLiveDetail[index];

            try {
                if (detail.event && Array.isArray(detail.event.odds)) {
                    detail.event.odds.forEach(marketGroup => {
                        const markets = Array.isArray(marketGroup) ? marketGroup : [marketGroup];
                        markets.forEach(m => {
                            if (m && Array.isArray(m.outcome)) {
                                m.outcome.forEach(odd => {
                                    if (odd && odd.unique_id) delete oddsDetailLookup[odd.unique_id];
                                });
                            }
                        });
                    });
                }
            } catch (e) {
                console.error("Safe cleanup failed in Live UPDATE_ODDS_EVENT_LIVE_DETAIL:", e);
            }

            detail.event.odds = payload.event.odds;

            if (payload.event.odds && Array.isArray(payload.event.odds)) {
                payload.event.odds.forEach(marketGroup => {
                    if (Array.isArray(marketGroup)) {
                        marketGroup.forEach(m => mapDetailOddsToLookup(m, detail.event.id));
                    } else {
                        mapDetailOddsToLookup(marketGroup, detail.event.id);
                    }
                });
            }
            state.eventsLiveDetail.splice(index, 1, detail);
        } else {
            console.error("ATTENZIONE: Evento non trovato nello store! ID cercato:", targetId);
        }
    },

    CLEAR_ODDS_EVENT_LIVE_DETAIL(state, targetId) {
        const index = state.eventsLiveDetail.findIndex(e => e.source === targetId || (e.event && e.event.id === targetId));
        if (index !== -1) {
            const detail = state.eventsLiveDetail[index];

            try {
                if (detail.event && Array.isArray(detail.event.odds)) {
                    detail.event.odds.forEach(marketGroup => {
                        const markets = Array.isArray(marketGroup) ? marketGroup : [marketGroup];
                        markets.forEach(m => {
                            if (m && Array.isArray(m.outcome)) {
                                m.outcome.forEach(odd => {
                                    if (odd && odd.unique_id) delete oddsDetailLookup[odd.unique_id];
                                });
                            }
                        });
                    });
                }
            } catch(e) {
                console.error("Safe cleanup failed in Live CLEAR_ODDS_EVENT_LIVE_DETAIL:", e);
            }

            detail.event.odds = [];
            state.eventsLiveDetail.splice(index, 1, detail);
        }
    },

    REMOVE_EVENT_LIVE_DETAIL(state, source) {
        try {
            const eventToRemove = state.eventsLiveDetail.find(e => e.source === source || (e.event && e.event.id === source));
            if (eventToRemove && eventToRemove.event && Array.isArray(eventToRemove.event.odds)) {
                eventToRemove.event.odds.forEach(marketGroup => {
                    const markets = Array.isArray(marketGroup) ? marketGroup : [marketGroup];
                    markets.forEach(m => {
                        if (m && Array.isArray(m.outcome)) {
                            m.outcome.forEach(odd => {
                                if (odd && odd.unique_id) delete oddsDetailLookup[odd.unique_id];
                            });
                        }
                    });
                });
            }
        } catch(e) {
            console.error("Safe cleanup failed in Live REMOVE_EVENT_LIVE_DETAIL:", e);
        }

        state.eventsLiveDetail = state.eventsLiveDetail.filter(e => e.source !== source && (!e.event || e.event.id !== source));

        const id = String(source);
        if (_goalTimers.has(id)) {
            clearTimeout(_goalTimers.get(id));
            _goalTimers.delete(id);
        }
        delete eventsDetailLookup[source];

        if (state.eventsLiveDetail.length === 0) {
            oddsDetailLookup = {};
        }
    },

    UPDATE_LIVESCORE_FROM_SOCKET(state, payload) {
        const updates = Array.isArray(payload) ? payload : [payload];
        for (let i = 0; i < updates.length; i++) {
            const change = updates[i];
            const event = eventsDetailLookup[change.id];

            if (!event) continue;

            if (isPayloadStale(event, change)) continue;

            if (change.lastUpdate) {
                Vue.set(event, 'lastUpdate', change.lastUpdate);
            }
            if (change.status != null) {
                event.status = String(change.status);
            }

            if (change.livescore) {
                const incomingData = Array.isArray(change.livescore) ? change.livescore[0] : change.livescore;
                if (!incomingData) continue;

                const goalPosition = injectGoalIfScoreChanged(event, {...change, livescore: incomingData});
                if (goalPosition !== null) {
                    if (_goalTimers.has(change.id)) clearTimeout(_goalTimers.get(change.id));
                    const eventId = change.id;
                    _goalTimers.set(eventId, setTimeout(() => {
                        const evt = eventsDetailLookup[eventId];
                        if (evt?.livescore?.livescoreExtraData) {
                            Vue.set(evt.livescore, 'livescoreExtraData',
                                evt.livescore.livescoreExtraData.filter(e => e.type !== 9)
                            );
                        }
                        _goalTimers.delete(eventId);
                    }, 10000));
                }

                if (!event.livescore) {
                    Vue.set(event, 'livescore', incomingData);
                } else {
                    if (incomingData.scoreboard) {
                        Vue.set(event.livescore, 'scoreboard',
                            assign({}, event.livescore.scoreboard || {}, incomingData.scoreboard)
                        );
                    }
                    if (incomingData.lastUpdate) Vue.set(event.livescore, 'lastUpdate', incomingData.lastUpdate);
                    if (incomingData.livescoreExtraData) event.livescore.livescoreExtraData = incomingData.livescoreExtraData;
                    if (incomingData.periods) Vue.set(event.livescore, 'periods', incomingData.periods);
                    if (incomingData.statistics) Vue.set(event.livescore, 'statistics', incomingData.statistics);
                    if (incomingData.matchStatus) Vue.set(event.livescore, 'matchStatus', incomingData.matchStatus);
                    // Same as previewLive: the MatchTracker reads the provider's
                    // current incident from here.
                    if (incomingData.currentIncident && Object.keys(incomingData.currentIncident).length > 0) {
                        Vue.set(event.livescore, 'currentIncident', incomingData.currentIncident);
                    }
                }
                // Timestamp of THIS livescore message (0 = the message had
                // none): the MatchTracker announces an incident only from a
                // message newer than the one behind the incident on screen.
                // Set on every message, so it never carries over from an
                // older one the way `lastUpdate` does.
                Vue.set(event.livescore, 'lastMessageUpdate', extractLastUpdate({...change, livescore: incomingData}));
            }
        }
    },

    UPDATE_ODDS_FROM_SOCKET(state, payload) {
        if (!payload || !Array.isArray(payload)) return;

        // Track markets touched by this socket batch so KEY re-apply runs
        // ONCE per market at the end, not per single outcome update.
        // Set dedups automatically → O(dirty markets) not O(updates).
        const dirtyMarkets = new Set();

        for (let i = 0; i < payload.length; i++) {
            const eventPayload = payload[i];
            if (eventPayload.odds && eventPayload.odds.set) {
                const oddChanges = eventPayload.odds.set;

                for (let j = 0; j < oddChanges.length; j++) {
                    const changeItem = oddChanges[j];
                    const uniqueId = changeItem.unique_id;
                    const attr = changeItem._attributes;
                    const lookupRef = oddsDetailLookup[uniqueId];

                    const inTime = attr?.lastUpdate ? new Date(attr.lastUpdate).getTime() : 0;

                    if (lookupRef && attr) {
                        const targetOdd = lookupRef.odd;

                        if (targetOdd.lastUpdate && inTime > 0 && inTime < targetOdd.lastUpdate) {
                            continue;
                        }

                        const newValue = parseFloat(attr.odds);
                        const oldValue = parseFloat(targetOdd.outcome_odd_value);

                        if (newValue > oldValue) Vue.set(targetOdd, 'type_change', 'up');
                        else if (newValue < oldValue) Vue.set(targetOdd, 'type_change', 'down');

                        // The socket carries the RAW provider value → update
                        // `outcome_odd_original` (baseline for KEY re-apply)
                        // AND `outcome_odd_value` (display). If KEY is enabled
                        // for live, the reapply pass below will overwrite the
                        // display value with the rescaled figure.
                        targetOdd.outcome_odd_original = newValue;
                        targetOdd.outcome_odd_value = newValue;
                        targetOdd.status = parseInt(attr.status);
                        // Freeze-suspend authoritative wins over provider
                        // socket updates: an outcome that was frozen at
                        // load time (frozenApplied === true) stays with
                        // its already-served `active` state even if the
                        // provider now flips status=1. The next silent
                        // refresh v2 will re-run the freeze resolver and
                        // confirm/clear the flag from the DB rule state.
                        if (targetOdd.frozenApplied !== true) {
                            targetOdd.active = (targetOdd.status === 1);
                        }
                        // Boost invalidation on socket tick — the socket
                        // payload carries the RAW provider odd, so any
                        // previously-applied boost is stale until the next
                        // full-refresh from the server (SILENT_PATCH below,
                        // or a re-fetch of the event) confirms it via a
                        // fresh boost resolver pass. Without this the badge
                        // sticks after e.g. a prematch → live transition
                        // where the boost rule only exists for prematch.
                        if (targetOdd.boostApplied === true) {
                            Vue.set(targetOdd, 'boostApplied', false);
                            Vue.set(targetOdd, 'boostPercent', null);
                            Vue.set(targetOdd, 'oddsPreBoost', null);
                        }
                        if (inTime > 0) targetOdd.lastUpdate = inTime;

                        // R.1 early bailout — skip markets without KEY rule.
                        if (lookupRef.market && lookupRef.market.keyApplied != null) {
                            dirtyMarkets.add(lookupRef.market);
                        }
                    }
                }
            }
        }

        // KEY live re-apply on every dirty market. No-op when
        // enableKeyEvaluationLive is false or the market wasn't KEY-adjusted
        // to begin with (market.keyApplied == null) — cost is a Set iterate
        // + early return per market.
        if (dirtyMarkets.size > 0) reapplyKeyOnMarkets(dirtyMarkets);
    },

    SILENT_PATCH_LIVE_DETAIL(state, freshEventData) {
        if (!freshEventData || !freshEventData.event) return;
        const freshEvent = freshEventData.event;

        const existing = eventsDetailLookup[freshEvent.id];
        if (!existing) return;

        if (!isPayloadStale(existing, freshEvent)) {

            if (freshEvent.lastUpdate) {
                Vue.set(existing, 'lastUpdate', freshEvent.lastUpdate);
            }
            if (freshEvent.status != null) {
                existing.status = String(freshEvent.status);
            }

            if (freshEvent.livescore) {
                if (!existing.livescore) {
                    Vue.set(existing, 'livescore', freshEvent.livescore);
                } else {
                    if (freshEvent.livescore.scoreboard) {
                        Vue.set(existing.livescore, 'scoreboard',
                            assign({}, existing.livescore.scoreboard || {}, freshEvent.livescore.scoreboard)
                        );
                    }
                    if (freshEvent.livescore.lastUpdate) Vue.set(existing.livescore, 'lastUpdate', freshEvent.livescore.lastUpdate);
                    if (freshEvent.livescore.periods) Vue.set(existing.livescore, 'periods', freshEvent.livescore.periods);
                    if (freshEvent.livescore.matchStatus) Vue.set(existing.livescore, 'matchStatus', freshEvent.livescore.matchStatus);
                }
            }
        }

        if (!freshEvent.odds || !Array.isArray(freshEvent.odds)) return;

        freshEvent.odds.forEach(marketGroup => {
            const markets = Array.isArray(marketGroup) ? marketGroup : [marketGroup];
            markets.forEach(market => {
                if (!market || !Array.isArray(market.outcome)) return;
                market.outcome.forEach(outcome => {
                    let targetOdd = null;
                    let targetMarket = null;

                    if (outcome.unique_id) {
                        const ref = oddsDetailLookup[outcome.unique_id];
                        if (ref && (ref.eventId == null || ref.eventId === freshEvent.id)) {
                            targetOdd = ref.odd;
                            targetMarket = ref.market;
                        }
                    }

                    if (!targetOdd) {
                        const existingOdds = existing.odds;
                        if (Array.isArray(existingOdds)) {
                            const freshMktKey = market.unique_id || String(market.marketId || market.id || '');
                            outer: for (let mk = 0; mk < existingOdds.length; mk++) {
                                const groupOrMarket = existingOdds[mk];
                                const candidates = Array.isArray(groupOrMarket) ? groupOrMarket : [groupOrMarket];
                                for (let ci = 0; ci < candidates.length; ci++) {
                                    const exMkt = candidates[ci];
                                    if (!exMkt || !Array.isArray(exMkt.outcome)) continue;
                                    const exMktKey = exMkt.unique_id || String(exMkt.marketId || exMkt.id || '');
                                    if (freshMktKey && exMktKey && freshMktKey !== exMktKey) continue;
                                    for (let oc = 0; oc < exMkt.outcome.length; oc++) {
                                        const exOdd = exMkt.outcome[oc];
                                        const sameId = (
                                            (outcome.outcome_id != null && String(exOdd.outcome_id) === String(outcome.outcome_id)) ||
                                            (outcome.id_outcome != null && String(exOdd.id_outcome) === String(outcome.id_outcome)) ||
                                            (outcome.ext_id != null && String(exOdd.ext_id) === String(outcome.ext_id)) ||
                                            (outcome.outcomeId != null && String(exOdd.outcomeId) === String(outcome.outcomeId))
                                        );
                                        const sameName = !sameId &&
                                            (exOdd.name || exOdd.outcomeName) === (outcome.name || outcome.outcomeName);
                                        if (sameId || sameName) {
                                            targetOdd = exOdd;
                                            targetMarket = exMkt;
                                            if (outcome.unique_id) {
                                                oddsDetailLookup[outcome.unique_id] = {
                                                    odd: exOdd,
                                                    market: exMkt,
                                                    eventId: freshEvent.id
                                                };
                                            }
                                            break outer;
                                        }
                                    }
                                }
                            }
                        }
                    }

                    if (!targetOdd) return;

                    const inTime = outcome.lastUpdate ? new Date(outcome.lastUpdate).getTime() : 0;
                    if (targetOdd.lastUpdate && inTime > 0 && inTime < targetOdd.lastUpdate) {
                        return;
                    }

                    const newVal = parseFloat(outcome.outcome_odd_value);
                    const oldVal = parseFloat(targetOdd.outcome_odd_value);
                    if (newVal > oldVal) Vue.set(targetOdd, 'type_change', 'up');
                    else if (newVal < oldVal) Vue.set(targetOdd, 'type_change', 'down');

                    targetOdd.outcome_odd_value = newVal;
                    targetOdd.status = outcome.status;
                    targetOdd.active = outcome.active;
                    if (inTime > 0) targetOdd.lastUpdate = inTime;

                    if (outcome.mainLine != null) targetOdd.mainLine = outcome.mainLine;

                    // Freeze markers MUST be kept in sync with the server here.
                    // Without this, if the freeze rule was created AFTER the
                    // page loaded, the silent refresh brings `active: false`
                    // (odd shows suspended) but `frozenApplied` stays undefined
                    // locally — and the very next socket ODDS_CHANGE_LIVE
                    // then flips `active` back to true because the guard
                    // (`if (frozenApplied !== true) active = status===1`)
                    // does not fire. Result: the frozen odd reopens on-screen
                    // even though rocky-bets rejects any bet placed on it.
                    // Symmetric case: when a freeze rule is REMOVED, the
                    // payload no longer carries `frozenApplied` for this
                    // outcome — we must clear the local flag or the next
                    // socket update would keep the odd artificially closed.
                    if (outcome.frozenApplied === true) {
                        Vue.set(targetOdd, 'frozenApplied', true);
                    } else if (targetOdd.frozenApplied === true) {
                        Vue.set(targetOdd, 'frozenApplied', false);
                    }
                    // FRZ.2 — mirror the change into the shared registry.
                    syncFrozen(targetOdd.unique_id, targetOdd.frozenApplied === true);

                    // KEY re-apply sync — propagate the fresh market-level
                    // `keyApplied`/`keyFallback` from the silent refresh to
                    // the existing market so the next socket ODDS_CHANGE_LIVE
                    // rescales using the current server value, not the one
                    // cached at page open. Without this, an admin edit of a
                    // KEY rule while the player has the screen open would
                    // show the correct odds only until the very next socket
                    // update, which would reapply the stale keyApplied and
                    // drift the odds back to the old rescale. Symmetric case:
                    // rule DELETED admin-side → fresh market has no
                    // `keyApplied` → we null it locally so socket updates
                    // return to serving raw provider values.
                    if (targetMarket) {
                        if (market.keyApplied != null) {
                            if (targetMarket.keyApplied !== market.keyApplied) {
                                Vue.set(targetMarket, 'keyApplied', market.keyApplied);
                            }
                        } else if (targetMarket.keyApplied != null) {
                            Vue.set(targetMarket, 'keyApplied', null);
                        }
                        if (market.keyFallback != null) {
                            if (targetMarket.keyFallback !== market.keyFallback) {
                                Vue.set(targetMarket, 'keyFallback', market.keyFallback);
                            }
                        } else if (targetMarket.keyFallback != null) {
                            Vue.set(targetMarket, 'keyFallback', null);
                        }
                    }

                    // BOOST silent-refresh sync — mirror the same pattern as
                    // KEY/FREEZE. Without this, an admin edit of a boost rule
                    // (create/delete/percent change) while the player has the
                    // screen open would show the fresh boosted odds on the
                    // very next silent refresh but the BADGE would stay stale.
                    // Symmetric case: rule DELETED → payload no longer carries
                    // `boostApplied` → clear the local flag so the badge
                    // disappears immediately.
                    if (outcome.boostApplied === true) {
                        Vue.set(targetOdd, 'boostApplied', true);
                        if (outcome.boostPercent != null) {
                            Vue.set(targetOdd, 'boostPercent', Number(outcome.boostPercent));
                        }
                    } else if (targetOdd.boostApplied === true) {
                        Vue.set(targetOdd, 'boostApplied', false);
                        Vue.set(targetOdd, 'boostPercent', null);
                    }
                });
            });
        });
    },

    RESET_TYPE_CHANGES() {
        for (const key in oddsDetailLookup) {
            const ref = oddsDetailLookup[key];
            if (ref?.odd?.type_change != null) {
                Vue.set(ref.odd, 'type_change', null);
            }
        }
    },

    MARK_EVENT_LIVE_DETAIL_FINISHED(state, source) {
        const item = state.eventsLiveDetail.find(e => e.source === source || (e.event && e.event.id === source));
        if (item) Vue.set(item, 'matchFinished', true);
    },

    SET_EVENTS_LIVE_DETAIL_ACTIVE(state, payload) {
        state.eventsLiveDetailActive = payload;
    },
    ADD_EVENTS_LIVE_DETAIL_ACTIVE(state, source) {
        if (!state.eventsLiveDetailActive.includes(source)) state.eventsLiveDetailActive.push(source);
    },
    REMOVE_EVENTS_LIVE_DETAIL_ACTIVE(state, source) {
        state.eventsLiveDetailActive = state.eventsLiveDetailActive.filter(id => id !== source);
    },
    SET_EVENTS_LIVE_DETAIL_PARAMS(state, {source, sportId, tournamentId, groupId}) {
        Vue.set(state.eventsLiveDetailParams, source, {sportId, tournamentId, groupId: groupId ?? null});
    },
    SET_MARKETS_GROUPS_DETAIL_LIVE(state, payload) {
        state.marketsGroupsDetailLive = payload;
    }
};

const actions = {
    async setLoadingDetail(context, status) {
        context.commit('SET_LOADING', status);
    },

    async fetchEventLiveDetailV5(context, payload) {
        if (!context.state.eventsLiveDetailActive.includes(payload.source)) {
            if (!payload.skipHistory) {
                await context.dispatch('sportNavigation/pushHistory', null, {root: true});
            }
            context.commit('SET_EVENTS_LIVE_DETAIL_ACTIVE', [payload.source]);
            context.commit('SET_EVENTS_LIVE_DETAIL_PARAMS', {
                source: payload.source,
                sportId: payload.sport,
                tournamentId: payload.tournament,
                groupId: payload.group ?? null
            });
            context.commit('SET_LOADING', true);

            const apiUrl = context.rootGetters['settings/getUrlApi'];
            const token = context.rootGetters['settings/getToken'];
            const body = _buildDetailBody(context, payload.source, payload.sport, payload.tournament, payload.group, undefined, {
                homeId: payload.competitorHomeId,
                awayId: payload.competitorAwayId,
                home: payload.competitorHome,
                away: payload.competitorAway,
                sportName: payload.sportName,
                categoryName: payload.categoryName,
                tournamentName: payload.tournamentName,
            });

            try {
                const res = await axios.post(`${apiUrl}/v3/events/live/event-detail`, body, {headers: {Authorization: `Bearer ${token}`}});

                if (res.data.messages === "Success Operation" && res.data.event != null) {
                    if (res.data.marketsGroups) context.commit('SET_MARKETS_GROUPS_DETAIL_LIVE', res.data.marketsGroups);
                    const data_event = await eventsOddsHandler.refactorEventLiveDetailV3(res.data);
                    context.commit('SET_EVENT_LIVE_DETAIL', [data_event]);

                    const liveName = (data_event.event?.home && data_event.event?.away)
                        ? `${data_event.event.home} ${data_event.event.away}`
                        : (payload.competitorHome != null
                            ? `${payload.competitorHome} ${payload.competitorAway}`
                            : (payload.eventName || ''));

                    window.parent.postMessage({
                        type: 'eventOpened',
                        section: 'live',
                        eventId: payload.source,
                        sportId: payload.sport,
                        tournamentId: payload.tournament,
                        eventName: liveName,
                        eventNameBack: payload.eventName || null
                    }, "*");
                } else {
                    context.commit('SET_EVENT_LIVE_DETAIL', []);
                    context.dispatch('showAlertEventNotAvailable', payload.source);
                }
            } catch (error) {
                console.error("Error fetchEventLiveDetailV5:", error);
                context.commit('SET_EVENT_LIVE_DETAIL', []);
                context.dispatch('showAlertEventNotAvailable', payload.source);
            } finally {
                context.commit('SET_LOADING', false);
            }
        }
    },

    async fetchOddsGroupDetailLiveV3(context, payload) {
        context.commit('SET_EVENTS_LIVE_DETAIL_PARAMS', {
            source: payload.source,
            sportId: payload.sport,
            tournamentId: payload.tournament,
            groupId: payload.group ?? null
        });
        const apiUrl = context.rootGetters['settings/getUrlApi'];
        const token = context.rootGetters['settings/getToken'];
        const existingLive = context.state.eventsLiveDetail.find(d => d.source === payload.source || (d.event && d.event.id === payload.source));
        const lev = existingLive && existingLive.event;
        const body = _buildDetailBody(context, payload.source, payload.sport, payload.tournament, payload.group, payload.marketId, lev ? {
            homeId: lev.home_id, awayId: lev.away_id, home: lev.home, away: lev.away,
            sportName: lev.sportName, categoryName: lev.categoryName, tournamentName: lev.tournamentName,
        } : null);

        try {
            const res = await axios.post(`${apiUrl}/v3/events/live/event-detail`, body, {headers: {Authorization: `Bearer ${token}`}});
            if (res.data.messages === "Success Operation") {
                if (res.data.marketsGroups) context.commit('SET_MARKETS_GROUPS_DETAIL_LIVE', res.data.marketsGroups);
                if (res.data.event != null) {
                    const data_event = await eventsOddsHandler.refactorEventLiveDetailV3(res.data);
                    context.commit('UPDATE_ODDS_EVENT_LIVE_DETAIL', data_event);
                } else {
                    context.commit('CLEAR_ODDS_EVENT_LIVE_DETAIL', payload.source);
                }
            }
        } catch (error) {
            console.error("Error fetchOddsGroupDetailLiveV3:", error);
        }
    },

    async fetchAvailableMarketsLive(context, payload) {
        const requestPayload = {
            platformId: context.rootGetters['settings/getPlatformId'],
            sportId: payload.sport,
            eventId: payload.source,
            lang: context.rootGetters['settings/getLanguage'],
            product_id: 1
        };
        const apiUrl = context.rootGetters['settings/getUrlApi'];
        const token = context.rootGetters['settings/getToken'];
        try {
            const res = await axios.post(`${apiUrl}/v3/events/live/event-available-markets`, requestPayload, {headers: {Authorization: `Bearer ${token}`}});
            const list = Array.isArray(res.data) ? res.data : (res.data && Array.isArray(res.data.markets) ? res.data.markets : []);
            context.commit('SET_AVAILABLE_MARKETS', list);
        } catch (error) {
            console.error("Error fetchAvailableMarketsLive:", error);
        }
    },

    async removeEventLiveDetail(context, source) {
        context.commit('REMOVE_EVENT_LIVE_DETAIL', source);
        context.commit('REMOVE_EVENTS_LIVE_DETAIL_ACTIVE', source);
        context.commit('SET_MARKETS_GROUPS_DETAIL_LIVE', []);
        Vue.delete(context.state.eventsLiveDetailParams, source);
        window.parent.postMessage({type: 'liveDetailClosed'}, "*");
    },

    async silentRefreshLiveDetail(context) {
        const activeSources = context.state.eventsLiveDetailActive;
        if (!activeSources || activeSources.length === 0) return;

        const apiUrl = context.rootGetters['settings/getUrlApi'];
        const token = context.rootGetters['settings/getToken'];

        const requests = activeSources.map(source => {
            const savedParams = context.state.eventsLiveDetailParams[source];
            const detail = context.state.eventsLiveDetail.find(
                e => e.source === source || (e.event && e.event.id === source)
            );

            if (detail?.matchFinished) return Promise.resolve();

            const sportId = savedParams?.sportId || detail?.event?.sportId || detail?.sport?.id || '';
            const tournamentId = savedParams?.tournamentId || detail?.event?.tournamentId || detail?.tournament?.id || '';
            const groupId = savedParams?.groupId ?? null;

            if (groupId === 'favourites' || groupId === 'betbuilder') return Promise.resolve();

            const body = _buildDetailBody(context, source, sportId, tournamentId, groupId);

            return axios.post(`${apiUrl}/v3/events/live/event-odds-refresh`, body, {headers: {Authorization: `Bearer ${token}`}})
                .then(async res => {
                    if (res.data.messages === 'Success Operation' && res.data.event != null) {
                        const freshData = await eventsOddsHandler.refactorEventLiveDetailV3(res.data);
                        context.commit('SILENT_PATCH_LIVE_DETAIL', freshData);
                    } else {
                        await _handleDetailEventMissing(context, source, apiUrl, token);
                    }
                })
                .catch(async () => {
                    await _handleDetailEventMissing(context, source, apiUrl, token);
                });
        });

        await Promise.all(requests);
        _scheduleTypeChangeReset(context.commit);
    },

    // --- V2: Ultra-Lightweight Polling (Master Cache + In-Memory Filter) ---
    async silentRefreshLiveDetailV2(context) {
        const activeSources = context.state.eventsLiveDetailActive;
        if (!activeSources || activeSources.length === 0) return;

        const apiUrl = context.rootGetters['settings/getUrlApi'];
        const token = context.rootGetters['settings/getToken'];

        const requests = activeSources.map(source => {
            const detail = context.state.eventsLiveDetail.find(
                e => e.source === source || (e.event && e.event.id === source)
            );

            if (detail?.matchFinished) return Promise.resolve();

            let currentMarketIds = [];
            if (detail && detail.event && Array.isArray(detail.event.odds)) {
                detail.event.odds.forEach(marketGroup => {
                    const markets = Array.isArray(marketGroup) ? marketGroup : [marketGroup];
                    markets.forEach(m => {
                        if (m.id) currentMarketIds.push(String(m.id));
                        else if (m.marketId) currentMarketIds.push(String(m.marketId));
                    });
                });
            }

            currentMarketIds = [...new Set(currentMarketIds)];

            if (currentMarketIds.length === 0) return Promise.resolve();

            const body = {
                platformId: context.rootGetters['settings/getPlatformId'],
                eventId: source,
                marketIds: currentMarketIds
            };

            return axios.post(`${apiUrl}/v3/events/live/event-odds-refresh-v2`, body, {headers: {Authorization: `Bearer ${token}`}})
                .then(async res => {
                    if (res.data.messages === 'Success Operation' && res.data.event != null) {

                        const receivedOdds = res.data.event.odds || [];
                        const evaluatedIds = res.data.event.evaluatedMarketIds || [];
                        const receivedIds = receivedOdds.map(m => String(m.id));

                        const missingIds = evaluatedIds.filter(id => !receivedIds.includes(String(id)));

                        if (missingIds.length > 0) {
                            missingIds.forEach(missingId => {
                                let existingOutcomes = [];

                                if (detail && detail.event && Array.isArray(detail.event.odds)) {
                                    detail.event.odds.forEach(mg => {
                                        const mkts = Array.isArray(mg) ? mg : [mg];
                                        mkts.forEach(m => {
                                            if (String(m.id || m.marketId) === String(missingId) && Array.isArray(m.outcome)) {
                                                existingOutcomes = existingOutcomes.concat(m.outcome);
                                            }
                                        });
                                    });
                                }

                                if (existingOutcomes.length > 0) {
                                    receivedOdds.push({
                                        id: missingId,
                                        unique_id: missingId,
                                        outcome: existingOutcomes.map(o => ({
                                            unique_id: o.unique_id,
                                            outcome_id: o.outcome_id,
                                            outcome_odd_value: o.outcome_odd_value || 1,
                                            status: 2,
                                            active: false
                                        }))
                                    });
                                }
                            });
                            res.data.event.odds = receivedOdds;
                        }

                        context.commit('SILENT_PATCH_LIVE_DETAIL', res.data);
                    } else {
                        await _handleDetailEventMissing(context, source, apiUrl, token);
                    }
                })
                .catch(async () => {
                    await _handleDetailEventMissing(context, source, apiUrl, token);
                });
        });

        await Promise.all(requests);
        _scheduleTypeChangeReset(context.commit);
    },

    async pollLivescore(context, {eventId, provider}) {
        const apiUrl = context.rootGetters['settings/getUrlApi'];
        const token = context.rootGetters['settings/getToken'];
        try {
            const res = await axios.post(
                `${apiUrl}/v3/events/live/event-livescore`,
                {eventId, provider},
                {headers: {Authorization: `Bearer ${token}`}}
            );
            if (res.data.messages === 'Success Operation' && res.data.livescore) {
                context.commit('UPDATE_LIVESCORE_FROM_SOCKET', [{id: String(eventId), livescore: res.data.livescore}]);
            }
        } catch (e) {
            console.error(e)
        }
    },

    async fetchFavouriteMarketsLiveDetail(context, payload) {
        const marketIds = loadPinnedMarkets();
        const apiUrl = context.rootGetters['settings/getUrlApi'];
        const token = context.rootGetters['settings/getToken'];

        context.commit('SET_EVENTS_LIVE_DETAIL_PARAMS', {
            source: payload.source,
            sportId: payload.sport,
            tournamentId: payload.tournament,
            groupId: 'favourites',
        });

        if (!marketIds.length) {
            context.commit('UPDATE_ODDS_EVENT_LIVE_DETAIL', {source: payload.source, event: {odds: []}});
            return;
        }

        const body = {
            platformId: context.rootGetters['settings/getPlatformId'],
            eventId: payload.source,
            sportId: payload.sport,
            tournamentId: payload.tournament,
            marketIds,
            lang: context.rootGetters['settings/getLanguage'],
        };

        try {
            const res = await axios.post(
                `${apiUrl}/v3/events/live/event-favourite-markets`, body,
                {headers: {Authorization: `Bearer ${token}`}}
            );
            if (res.data.messages === 'Success Operation' && res.data.event) {
                const data_event = await eventsOddsHandler.refactorEventLiveDetailV3(res.data);
                context.commit('UPDATE_ODDS_EVENT_LIVE_DETAIL', data_event);
            } else {
                context.commit('UPDATE_ODDS_EVENT_LIVE_DETAIL', {source: payload.source, event: {odds: []}});
            }
        } catch (error) {
            console.error('Error fetchFavouriteMarketsLiveDetail:', error);
        }
    },

    /**
     * "Combo" tab of the live detail: bet builder markets of the event in progress.
     * rocky-api serves live markets and live odds for a live event (and an empty answer
     * when the live bet builder is not allowed); the polling refresh keeps them up to
     * date by market id like any other tab.
     */
    async fetchBetBuilderMarketsLive(context, payload) {
        const apiUrl = context.rootGetters['settings/getUrlApi'];
        const token = context.rootGetters['settings/getToken'];

        context.commit('SET_EVENTS_LIVE_DETAIL_PARAMS', {
            source: payload.source,
            sportId: payload.sport,
            tournamentId: payload.tournament,
            groupId: 'betbuilder',
        });

        const body = {
            platformId: context.rootGetters['settings/getPlatformId'],
            eventId: payload.source,
            sportId: payload.sport,
            categoryId: payload.category,
            tournamentId: payload.tournament,
            lang: context.rootGetters['settings/getLanguage'],
            page: 1,
            limit: 100,
        };

        try {
            const res = await axios.post(
                `${apiUrl}/v3/events/bet-builder-markets`, body,
                {headers: {Authorization: `Bearer ${token}`}}
            );
            if (res.data.messages === 'Success Operation' && res.data.event) {
                const data_event = await eventsOddsHandler.refactorEventLiveDetailV3(res.data);
                context.commit('UPDATE_ODDS_EVENT_LIVE_DETAIL', data_event);
            } else {
                context.commit('UPDATE_ODDS_EVENT_LIVE_DETAIL', {source: payload.source, event: {odds: []}});
            }
        } catch (error) {
            console.error('Error fetchBetBuilderMarketsLive:', error);
            context.commit('UPDATE_ODDS_EVENT_LIVE_DETAIL', {source: payload.source, event: {odds: []}});
        }
    },

    async showAlertEventNotAvailable(context, source) {
        context.dispatch('settings/setSnackbar', true, {root: true});
        context.dispatch('settings/setSnackbarText', 'eventsNoLongerAvailable', {root: true});
        context.commit('REMOVE_EVENTS_LIVE_DETAIL_ACTIVE', source);
        setTimeout(() => context.dispatch('settings/setSnackbar', false, {root: true}), 2000);
    },

    socketUpdateOdds({commit}, payload) {
        commit('UPDATE_ODDS_FROM_SOCKET', payload);
        _scheduleTypeChangeReset(commit);
    }
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
};